import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  CheckCircle2,
  Upload,
  ShieldCheck,
  Building,
  FileCheck,
  ArrowRight,
  Phone,
} from 'lucide-react';
import LandingNav, { PHONE_DISPLAY, PHONE_TEL } from '@/components/LandingNav';
import LandingFooter from '@/components/LandingFooter';

const trackGA = (event, data = {}) => {
  if (window.gtag) window.gtag('event', event, data);
};

/**
 * Shared Tier 2 city SEO page template.
 * Renders structured local content + funnels traffic into /get-quote.
 *
 * Props:
 *  - city: city config object from tier2Cities.js
 */
export default function Tier2CityPage({ city }) {
  useEffect(() => {
    // Set document title for SEO + GA4 page_view title
    const prevTitle = document.title;
    const prevMeta = document.querySelector('meta[name="description"]')?.getAttribute('content');
    document.title = `Impact Windows in ${city.city}: Pricing, Permits & Installation | Snap Windows`;
    let metaEl = document.querySelector('meta[name="description"]');
    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', 'description');
      document.head.appendChild(metaEl);
    }
    metaEl.setAttribute('content', city.metaDescription);

    trackGA('view_tier2_city', {
      event_category: 'seo_traffic',
      event_label: city.slug,
    });

    return () => {
      document.title = prevTitle;
      if (prevMeta && metaEl) metaEl.setAttribute('content', prevMeta);
    };
  }, [city.slug, city.city, city.metaDescription]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <LandingNav logoTo="/get-quote" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-ocean-100)]/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-10 md:pt-16 md:pb-12">
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-[11px] uppercase tracking-wider border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] bg-white"
          >
            <MapPin className="h-3 w-3 mr-1 inline" />
            {city.county} County • South Florida
          </Badge>
          <h1
            className="mt-4 font-heading text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] tracking-tight text-[var(--brand-slate-900)]"
            data-testid="hero-headline"
          >
            Impact Windows in {city.city}: Pricing, Permits, and Installation
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[var(--brand-slate-600)] max-w-2xl">
            Real installed pricing, permitting through {city.county} County, and HOA-ready specs — without an in-home sales appointment.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              size="lg"
              className="h-13 px-7 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md hover:shadow-lg"
              data-testid="hero-cta-get-quote"
            >
              <Link
                to="/get-quote"
                onClick={() => trackGA('seo_cta_to_quote', { event_category: 'seo_funnel', event_label: `${city.slug}_hero` })}
              >
                <Upload className="mr-2 h-5 w-5" />
                Get {city.city} Pricing
              </Link>
            </Button>
            <a
              href={PHONE_TEL}
              className="inline-flex items-center justify-center h-13 px-6 rounded-xl text-base font-semibold border-2 border-[hsl(var(--border))] text-[var(--brand-slate-800)] hover:bg-[hsl(var(--secondary))]"
              onClick={() => trackGA('phone_call_click', { event_category: 'engagement', event_label: `tier2_${city.slug}_hero` })}
              data-testid="hero-call-cta"
            >
              <Phone className="mr-2 h-5 w-5" />
              Or call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="py-10 sm:py-14 bg-[hsl(var(--secondary))]/40 border-y border-[hsl(var(--border))]" data-testid="quick-answer-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Quick answer
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
            What do impact windows cost in {city.city}?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--brand-slate-700)] leading-relaxed">
            {city.quickAnswer}
          </p>
        </div>
      </section>

      {/* Local context */}
      <section className="py-12 sm:py-16" data-testid="local-context-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Local context
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
            What makes {city.city} installs different
          </h2>

          <div className="mt-7 grid sm:grid-cols-2 gap-4 sm:gap-5">
            {city.localContext.map((point, idx) => (
              <Card key={idx} className="rounded-2xl border border-[hsl(var(--border))] bg-white" data-testid={`local-context-${idx}`}>
                <CardContent className="p-5 sm:p-6 flex gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center shrink-0">
                    <Building className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                  </div>
                  <p className="text-sm sm:text-[15px] text-[var(--brand-slate-700)] leading-relaxed">
                    {point}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* HOA / Permit callout */}
          <Card className="mt-6 rounded-2xl border-2 border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/15" data-testid="hoa-callout">
            <CardContent className="p-5 sm:p-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-[var(--brand-ocean-700)] flex items-center justify-center shrink-0">
                <FileCheck className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">
                  HOA & permit notes for {city.city}
                </h3>
                <p className="mt-1.5 text-sm sm:text-[15px] text-[var(--brand-slate-700)] leading-relaxed">
                  {city.hoaNotes}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Real proof */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/40" data-testid="proof-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Real proof
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
            Recent South Florida projects
          </h2>
          <div className="mt-7 sm:mt-9 divide-y divide-[hsl(var(--border))]">
            {[
              { id: 'kendall', title: 'Kendall Full Home', stat: '~$28,400', desc: 'Complete impact window and door replacement using ES Windows' },
              { id: 'north-miami', title: 'North Miami Condo', stat: '~$25,000', desc: 'HOA-compliant installation with coastal-grade materials (Mr. Glass)' },
            ].map((cs) => (
              <div key={cs.id} className="py-5 sm:py-6 first:pt-0 last:pb-0" data-testid={`proof-item-${cs.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">{cs.title}</h3>
                  <span className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-ocean-700)] whitespace-nowrap">— {cs.stat}</span>
                </div>
                <p className="mt-1.5 text-sm sm:text-[15px] text-[var(--brand-slate-600)] leading-relaxed">{cs.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing context */}
      <section className="py-12 sm:py-16" data-testid="pricing-context-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Pricing context
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
            Most homes range from{' '}
            <span className="text-[var(--brand-ocean-700)]">$15K–$40K+</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--brand-slate-700)] leading-relaxed">
            Final pricing for {city.city} depends on number of openings, product tier (Good / Better / Premium), glass spec, and {city.county} County permitting. We give you all three options side-by-side.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--secondary))]/40" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl border-2 border-[var(--brand-ocean-600)] bg-white p-8 sm:p-12 text-center shadow-sm">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
              Get your {city.city} pricing without a sales visit
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[var(--brand-slate-700)] max-w-xl mx-auto leading-relaxed">
              Upload a few photos and we’ll send Good, Better, and Premium pricing for your home — usually within 24 hours.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                size="lg"
                className="h-14 px-8 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md hover:shadow-lg"
                data-testid="final-cta-button"
              >
                <Link
                  to="/get-quote"
                  onClick={() => trackGA('seo_cta_to_quote', { event_category: 'seo_funnel', event_label: `${city.slug}_final` })}
                >
                  <Upload className="mr-2 h-5 w-5" />
                  Get My Price
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[var(--brand-slate-600)]">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />No appointment</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />No pressure</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[var(--brand-ocean-700)]" />{city.county} County permits handled</span>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
